import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Snackbar, Switch, Tooltip, Typography } from '@mui/material';
import AdsApi from '../api/ads';
import { DEFAULT_SITE, SiteFilter, StatusFilter, listUrl } from '../components/ads/AdFilters';
import { METRIC_COLUMNS, NameCell } from '../components/ads/cells';
import StatusBadge from '../components/status/StatusBadge';
import { SelectFilter } from '../components/filters/Filters';
import ListPage from '../components/table/ListPage';
import { formatDate, fromNow } from '../utils/dates';
import { formatNumber } from '../utils/format';

const DEFAULTS = { site: DEFAULT_SITE };
const SYNC_OPTIONS = [{ value: 'enabled', label: 'Sync on' }, { value: 'disabled', label: 'Sync off' }];

export default function AdAccounts() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const toggleSync = async (row, { patchRow }) => {
    patchRow(row.id, { is_enabled: !row.is_enabled });
    try {
      await AdsApi.updateAccount(row.id, { is_enabled: !row.is_enabled });
    } catch (e) {
      patchRow(row.id, { is_enabled: row.is_enabled });
      setError(`Could not update ${row.name}: ${e.message}`);
    }
  };

  const columns = (list) => [
    {
      id: 'name', label: 'Ad account',
      render: (row) => <NameCell name={row.name} secondary={[row.act_id, row.currency].filter(Boolean).join(' · ')} />,
    },
    {
      id: 'is_enabled', label: 'Sync', render: (row) => (
        <Tooltip title={row.is_enabled ? 'Synced; turn off to stop syncing' : 'Not synced; turn on to sync'}>
          <Switch size="small" checked={row.is_enabled} onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleSync(row, list)} inputProps={{ 'aria-label': `Sync ${row.name}` }} />
        </Tooltip>
      ),
    },
    {
      id: 'account_status', label: 'Status', sort: false, render: (row) => <AccountStatus row={row} />,
    },
    { id: 'campaign_count', label: 'Campaigns', align: 'right', render: (row) => formatNumber(row.campaign_count) },
    { id: 'ad_count', label: 'Ads', align: 'right', render: (row) => formatNumber(row.ad_count) },
    ...METRIC_COLUMNS.filter((column) => ['spend', 'impressions', 'clicks', 'ctr'].includes(column.id)),
    {
      id: 'last_synced_at', label: 'Last synced', render: (row) => (row.last_synced_at ? (
        <Typography variant="body2" title={formatDate(row.last_synced_at)}>{fromNow(row.last_synced_at)}</Typography>
      ) : <Typography variant="body2" color="text.secondary">Never</Typography>),
    },
    { id: 'connection_name', label: 'Connected by', sort: false },
  ];

  return (
    <>
      <ListPage
        title="Ad accounts" description="Ad accounts reachable through your connections. Choose which ones are synced."
        fetchList={AdsApi.accounts} defaults={DEFAULTS} columns={columns}
        onRowClick={(row, query) => navigate(listUrl('/campaigns', query, { ad_account: row.id }))}
        emptyTitle="No ad accounts yet" emptyHint="Connect an account on the Connections page, then sync ad accounts."
        searchPlaceholder="Search name or act_ id"
        filters={({ query, setQuery }) => (
          <>
            <SiteFilter query={query} setQuery={setQuery} />
            <StatusFilter query={query} setQuery={setQuery} type="ad_account" />
            <SelectFilter label="Sync" value={query.sync} options={SYNC_OPTIONS} allLabel="All accounts" width={180}
                          onChange={(sync) => setQuery({ sync })} />
          </>
        )}
      />
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} variant="filled">{error}</Alert>
      </Snackbar>
    </>
  );
}

// label and tone come from the API (social.constants.AD_ACCOUNT_STATUSES)
function AccountStatus({ row }) {
  if (!row.account_status_label) return '—';
  return <StatusBadge tone={row.account_status_tone} label={row.account_status_label} />;
}
